export interface ClosingOfferRowDto {
  ticketId: number;
  ticketGuid: string;
  matterNumber: string | null;
  ticketNumber: string | null;
  citationDescription: string | null;
  matterType: string | null;
  statusName: string;
  statusStage: string;
  statusDate: string | null;
  daysInStage: number;

  userId: number | null;
  userGuid: string | null;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  courtName: string | null;

  assignedStaffName: string | null;
  assignedStaffSource: 'assigned' | 'last_contact' | null;

  lastContactAt: string | null;
  lastContactBy: string | null;
  lastContactKind: 'call' | 'note' | 'message' | null;
  lastContactSummary: string | null;
  daysSinceLastContact: number;

  hasOfferRecord: boolean;
  offerId: number | null;
  offerGuid: string | null;
  offerType: 'dsc' | 'deferral' | 'conviction' | 'appeal' | null;
  offerSource: string | null;
  deferralDays: number | null;
  deferralFeeUsd: number | null;
  dscAlsoRequired: boolean;
  dscDays: number | null;
  conditions: string[];
  reducedFrom: string | null;
  reducedTo: string | null;
  offerNotes: string | null;
  offerReceivedAt: string | null;

  conveyanceStatus: 'conveyed' | 'drafted' | 'unrecorded';
  conveyedAt: string | null;
  conveyedChannels: string | null;
  clientResponse: 'accepted' | 'rejected' | null;
  respondedAt: string | null;
  acceptanceSentAt: string | null;
}

export interface ClosingDeferralConditionDto {
  conditionId: number;
  conditionGuid: string;
  type: string;
  label: string;
  dueAt: string | null;
  completedAt: string | null;
  amountCents: number | null;
  hours: number | null;
  detail: string | null;
  isOverdue: boolean;
}

export interface ClosingDeferralRowDto {
  ticketId: number;
  ticketGuid: string;
  matterNumber: string | null;
  ticketNumber: string | null;
  citationDescription: string | null;
  matterType: string | null;
  statusName: string;
  statusStage: string;
  statusDate: string | null;
  daysInStage: number;

  userId: number | null;
  userGuid: string | null;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  courtName: string | null;

  assignedStaffName: string | null;
  assignedStaffSource: 'assigned' | 'last_contact' | null;

  lastContactAt: string | null;
  lastContactBy: string | null;
  lastContactKind: 'call' | 'note' | 'message' | null;
  lastContactSummary: string | null;
  daysSinceLastContact: number;

  deferralSummary: string;
  deferralDays: number | null;

  hasConditionsRecorded: boolean;
  conditionsCount: number;
  conditionsCompletedCount: number;
  conditions: ClosingDeferralConditionDto[];
  nextDeadlineAt: string | null;
  hasOverdueDeadline: boolean;
  nextCourtDate: string | null;
}

export interface ClosingDeadlineItemDto {
  conditionId: number;
  conditionGuid: string;
  ticketId: number;
  ticketGuid: string;
  ticketNumber: string | null;
  matterNumber: string | null;
  clientName: string;
  courtName: string | null;
  type: string;
  label: string;
  dueAt: string; // YYYY-MM-DD
  timeWindow: 'overdue' | 'today' | 'next_7_days' | 'next_30_days' | 'future';
  amountCents: number | null;
  hours: number | null;
  detail: string | null;
  completedAt: string | null;
}

export interface ClosingResourceLinkDto {
  title: string;
  category: 'dsc' | 'driving_record';
  url: string;
  description: string;
  buttonLabel: string;
}

export interface ClosingHomeSummaryCountsDto {
  totalOfferStageCases: number;
  offersWithRecord: number;
  offersUnrecorded: number;
  offersDrafted: number;
  offersConveyed: number;
  offersAccepted: number;
  offersRejected: number;
  offersPendingResponse: number;

  totalDeferralStageCases: number;
  deferralWithConditions: number;
  deferralNoDeadlines: number;
  deferralOverdueCount: number;

  totalActiveDeadlines: number;
  deadlinesOverdue: number;
  deadlinesNext7Days: number;
  deadlinesNext30Days: number;
  undatedConditionsCount: number;
}

export interface ClosingHomeResponseDto {
  offers: ClosingOfferRowDto[];
  deferrals: ClosingDeferralRowDto[];
  deadlines: ClosingDeadlineItemDto[];
  undatedConditions: Array<{
    conditionId: number;
    conditionGuid: string;
    ticketId: number;
    ticketGuid: string;
    ticketNumber: string | null;
    clientName: string;
    type: string;
    label: string;
    detail: string | null;
  }>;
  resourceLinks: ClosingResourceLinkDto[];
  summaryCounts: ClosingHomeSummaryCountsDto;
  staffOptions: string[];
}

export interface FetchClosingHomeOptions {
  search?: string;
  staff?: string;
  offerStatus?: 'all' | 'drafted' | 'conveyed' | 'accepted' | 'rejected' | 'unrecorded';
  deferralStatus?: 'all' | 'with_conditions' | 'no_deadlines' | 'overdue';
}

export async function fetchClosingHome(
  options: FetchClosingHomeOptions = {},
): Promise<ClosingHomeResponseDto> {
  const params = new URLSearchParams();
  if (options.staff && options.staff !== 'all') {
    params.set('staff', options.staff);
  }
  if (options.offerStatus && options.offerStatus !== 'all') {
    params.set('offerStatus', options.offerStatus);
  }
  if (options.deferralStatus && options.deferralStatus !== 'all') {
    params.set('deferralStatus', options.deferralStatus);
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim());
  }

  const response = await fetch(
    `${process.env.API_HOST || ''}/api/v1/closing-home?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch closing home: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}
